import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { X, Check, Heart, ChevronLeft } from 'lucide-react';
import { mealAPI } from '../utils/api';
import { recordSetupTasteSignal, waitForSetupTasteSignals } from '../utils/tasteSignals';
import { useFavorites } from '../contexts/FavoritesContext';

/**
 * First-plan taste setup deck (UI rework).
 * Full-bleed photo cards, swipe or tap, a heart to Favorite+love+advance, a back
 * arrow to revisit prior cards, and a lightweight first-time coach-mark walkthrough.
 *
 * Both swipe and tap call the SAME choose() so signals/semantics are identical:
 *   swipe LEFT / red X   -> No  (negative cue)
 *   swipe RIGHT / green ✓ -> Yes (positive cue)
 *   heart -> REAL Favorite (add) + positive Yes cue, advancing only after the
 *            Favorite write succeeds. Choosing No on an already-Favorited recipe
 *            REMOVES the Favorite first so the account never ends both Favorited
 *            and answered No.
 *
 * demoMode: loads the public curated-primary sample and writes NOTHING — the
 * Favorite/remove is simulated locally (demoFav) for visual parity only.
 */
const WALK_KEY = 'taste_setup_walkthrough_seen_v1';

export function TasteSetupCards({ onComplete, demoMode = false }) {
  const { isFavorite, addFavorite, removeFavorite } = useFavorites();
  const [cards, setCards] = useState(null);   // null = loading
  const [idx, setIdx] = useState(0);
  const [answering, setAnswering] = useState(false);
  const [choices, setChoices] = useState({}); // idx -> 'yes' | 'no' | 'love'
  const [demoFav, setDemoFav] = useState(function () { return new Set(); }); // demo-only simulated favorites

  // Swipe (drag) — LEFT = No, RIGHT = Yes. Same behavior as the tap buttons.
  const SWIPE_THRESHOLD = 100;
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-240, 0, 240], [-14, 0, 14]);
  const yesOpacity = useTransform(x, [40, 130], [0, 1]);
  const noOpacity = useTransform(x, [-130, -40], [1, 0]);

  // Walkthrough coach-marks
  const cardRef = useRef(null);
  const controlsRef = useRef(null);
  const heartRef = useRef(null);
  const [walkStep, setWalkStep] = useState(-1);
  const [spot, setSpot] = useState(null);
  const steps = [
    { ref: cardRef, title: 'Swipe to choose', text: "Swipe right if it looks good, left if it doesn't." },
    { ref: controlsRef, title: 'Or just tap', text: 'You can tap these too.' },
    { ref: heartRef, title: 'Save a favorite', text: 'Love it? Tap the heart to save it.' },
  ];

  useEffect(function () {
    let alive = true;
    const loader = demoMode ? mealAPI.getTasteSetupDemoSample() : mealAPI.getTasteSetupSample();
    loader
      .then(function (res) {
        if (!alive) return;
        const data = res && res.data;
        const list = (data && data.cards) || [];
        if (!demoMode && !(data && data.catalog_ok)) {
          // Catalog/curation-integrity gap (curated slots missing, malformed
          // or unprovable). Whatever cards DID resolve are a partial deck, not
          // the taste setup: never show them as "1 of 1", never persist
          // completion. Continue to planning for THIS attempt only; the deck
          // is offered again on a later attempt.
          console.warn('[TasteSetup] catalog gap: ' + list.length + ' curated slot(s) resolved; setup skipped for now');
          onComplete();
          return;
        }
        if (!list.length) {
          if (!demoMode) {
            // A successful, COMPLETE evaluation of every slot genuinely found
            // zero eligible cards (allergen or personal-preference filtering,
            // not a catalog gap) -- persist completion and skip the deck.
            // Never shown again.
            mealAPI.completeTasteSetup()
              .then(function () { if (alive) onComplete(true); })
              .catch(function () { if (alive) onComplete(); }); // persistence failed -> fail open, retry next time
          } else {
            // Demo never marks complete.
            onComplete();
          }
          return;
        }
        setCards(list);
        let seen = false;
        try { seen = localStorage.getItem(WALK_KEY) === '1'; } catch (e) { seen = false; }
        if (demoMode || !seen) setTimeout(function () { if (alive) setWalkStep(0); }, 350);
      })
      .catch(function () { if (alive) onComplete(); });
    return function () { alive = false; };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Position the coach-mark spotlight over the current step's target.
  useEffect(function () {
    if (walkStep < 0) { setSpot(null); return; }
    const el = steps[walkStep] && steps[walkStep].ref.current;
    if (!el) return;
    // Short screens: the target (e.g. the Yes/No row) can sit below the fold
    // of the scrollable launcher sheet — bring it into view before measuring.
    if (typeof el.scrollIntoView === 'function') {
      try { el.scrollIntoView({ block: 'nearest' }); } catch (e) { /* ignore */ }
    }
    const t = setTimeout(function () {
      const r = el.getBoundingClientRect();
      setSpot({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, 40);
    return function () { clearTimeout(t); };
  }, [walkStep, cards, idx]);  // eslint-disable-line react-hooks/exhaustive-deps

  function endWalk() {
    setWalkStep(-1);
    // Demo mode never persists this flag: dismissing the PUBLIC demo's
    // walkthrough must not silently suppress the real first-run walkthrough
    // later in the same browser.
    if (!demoMode) {
      try { localStorage.setItem(WALK_KEY, '1'); } catch (e) { /* ignore */ }
    }
  }

  function cardIsFavorited(mealId) {
    return demoMode ? demoFav.has(mealId) : isFavorite(mealId);
  }

  // Record the setup answer (heart counts as Yes), then advance / complete.
  function commitAnswer(kind, card) {
    const isYes = kind !== 'no';
    setChoices(function (p) { const n = Object.assign({}, p); n[idx] = kind; return n; });
    x.set(0);  // recenter for the next card
    if (!demoMode) recordSetupTasteSignal(card.meal_id, isYes);  // ordered chain
    const isLast = idx >= cards.length - 1;
    if (isLast) {
      if (demoMode) { onComplete(true); return; }
      // Wait for the ORDERED setup-signal chain to fully settle (nonfatal — it
      // never rejects), THEN mark completion, THEN continue into planning.
      waitForSetupTasteSignals()
        .then(function () { return mealAPI.completeTasteSetup(); })
        .then(function () { onComplete(true); })
        .catch(function () { setAnswering(false); onComplete(); });
      return;
    }
    setIdx(idx + 1);
    setAnswering(false);
  }

  async function choose(kind) {
    if (answering || !cards || walkStep >= 0) return;
    const card = cards[idx];
    setAnswering(true);

    // --- Real Favorites side effects (demo simulates locally, zero writes) ---
    if (kind === 'love') {
      // Heart = "I like this enough to Favorite it." ADD (never toggle-off an
      // already-favorited recipe). Advance ONLY after the add succeeds.
      if (demoMode) {
        setDemoFav(function (p) { const n = new Set(p); n.add(card.meal_id); return n; });
      } else if (!isFavorite(card.meal_id)) {
        const ok = await addFavorite(card.meal_id);
        if (!ok) { setAnswering(false); return; }  // failure UX already shown; allow retry, don't advance
      }
    } else if (kind === 'no') {
      // Never leave the account both Favorited AND answered No: remove the
      // Favorite first; if removal fails, do NOT advance into a contradictory state.
      if (cardIsFavorited(card.meal_id)) {
        if (demoMode) {
          setDemoFav(function (p) { const n = new Set(p); n.delete(card.meal_id); return n; });
        } else {
          const ok = await removeFavorite(card.meal_id);
          if (!ok) { setAnswering(false); return; }
        }
      }
    }
    // kind === 'yes': Favorite (if any) may remain; Yes is not contradictory.

    commitAnswer(kind, card);
  }

  function handleDragEnd(_e, info) {
    if (answering || walkStep >= 0) return;
    if (info.offset.x > SWIPE_THRESHOLD) choose('yes');        // swipe RIGHT === Yes
    else if (info.offset.x < -SWIPE_THRESHOLD) choose('no');   // swipe LEFT  === No
    // else: dragSnapToOrigin returns the card to center (no answer).
  }

  function goBack() {
    if (answering || idx === 0 || walkStep >= 0) return;
    x.set(0);
    setIdx(idx - 1);
  }

  if (cards === null) {
    return (
      <div className="py-10 text-center" data-testid="taste-setup-loading">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
          <div className="animate-spin w-7 h-7 border-3 border-accent border-t-transparent rounded-full" />
        </div>
        <p className="text-sm text-muted-foreground">Setting up your taste picks...</p>
      </div>
    );
  }

  const card = cards[idx];
  const total = cards.length;
  const prior = choices[idx];  // set when revisiting via Back
  const placeBelow = !spot || spot.top < window.innerHeight / 2;

  return (
    <div className="space-y-4 pb-[max(0.5rem,env(safe-area-inset-bottom))]" data-testid="taste-setup-cards">
      <div className="text-center">
        <h3 className="font-heading font-bold text-lg mb-1">Does this look good to you?</h3>
        <p className="text-xs text-muted-foreground" data-testid="taste-setup-progress">
          {(idx + 1) + ' of ' + total}
        </p>
      </div>

      <div className="taste-card-frame" data-testid="taste-setup-card-frame">
      <AnimatePresence mode="wait">
        <motion.div
          ref={cardRef}
          key={card.meal_id + '-' + idx}
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: -12 }}
          transition={{ duration: 0.18 }}
          drag={answering || walkStep >= 0 ? false : 'x'}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.9}
          dragSnapToOrigin
          onDragEnd={handleDragEnd}
          style={{ x, rotate, touchAction: 'pan-y' }}
          whileTap={{ cursor: 'grabbing' }}
          className="relative aspect-[3/4] w-full rounded-3xl overflow-hidden shadow-lg cursor-grab active:cursor-grabbing select-none bg-secondary"
          data-testid="taste-setup-card"
        >
          <img
            src={card.image_url}
            alt={card.title}
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            data-testid="taste-setup-card-image"
          />
          {/* readability gradient toward the bottom */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />

          {/* Heart — love + advance (top-right) */}
          <button
            ref={heartRef}
            onClick={function () { choose('love'); }}
            disabled={answering}
            aria-label="Love it"
            className="absolute top-3 right-3 z-20 w-11 h-11 rounded-full bg-black/35 backdrop-blur-md flex items-center justify-center hover:bg-black/50 transition-colors disabled:opacity-50"
            data-testid="taste-setup-heart-btn"
          >
            <Heart size={22} className={cardIsFavorited(card.meal_id) ? 'text-red-500 fill-red-500' : 'text-white'} />
          </button>

          {/* Swipe direction stamps (centered, mutually exclusive by opacity) */}
          <motion.div style={{ opacity: yesOpacity }} className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="taste-setup-swipe-yes">
            <span className="px-5 py-2 border-4 border-green-400 text-green-300 rounded-2xl text-3xl font-extrabold tracking-wider rotate-[-12deg]">YES</span>
          </motion.div>
          <motion.div style={{ opacity: noOpacity }} className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="taste-setup-swipe-no">
            <span className="px-5 py-2 border-4 border-red-400 text-red-300 rounded-2xl text-3xl font-extrabold tracking-wider rotate-[12deg]">NO</span>
          </motion.div>

          {/* prior selection cue when revisiting */}
          {prior && (
            <div className="absolute top-3 left-3 z-20 px-3 py-1 rounded-full bg-black/45 backdrop-blur-md text-white text-xs font-medium" data-testid="taste-setup-prior-pill">
              {prior === 'no' ? 'You picked No' : prior === 'love' ? 'You loved this' : 'You picked Yes'}
            </div>
          )}

          {/* title overlaid near the bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-5 pointer-events-none">
            <p className="font-heading font-bold text-xl text-white drop-shadow" data-testid="taste-setup-card-title">
              {card.title}
            </p>
          </div>
        </motion.div>
      </AnimatePresence>
      </div>

      <div ref={controlsRef} className="flex items-center justify-center gap-6 pt-1">
        <button
          onClick={function () { choose('no'); }}
          disabled={answering}
          aria-label="No"
          className={'w-16 h-16 rounded-full bg-background border-2 flex items-center justify-center hover:border-red-400 hover:bg-red-50 transition-colors disabled:opacity-50 ' + (prior === 'no' ? 'border-red-400 bg-red-50' : 'border-border')}
          data-testid="taste-setup-no-btn"
        >
          <X size={26} className="text-red-500" />
        </button>
        <button
          onClick={function () { choose('yes'); }}
          disabled={answering}
          aria-label="Yes"
          className={'w-16 h-16 rounded-full bg-background border-2 flex items-center justify-center hover:border-green-400 hover:bg-green-50 transition-colors disabled:opacity-50 ' + (prior === 'yes' ? 'border-green-400 bg-green-50' : 'border-border')}
          data-testid="taste-setup-yes-btn"
        >
          <Check size={26} className="text-green-600" />
        </button>
      </div>

      {/* Back control — hidden on the first card */}
      <div className="h-6 flex items-center justify-center">
        {idx > 0 && (
          <button
            onClick={goBack}
            disabled={answering}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
            data-testid="taste-setup-back-btn"
          >
            <ChevronLeft size={14} /> Back
          </button>
        )}
      </div>

      {demoMode && (
        <div className="text-center">
          <button
            onClick={function () { setWalkStep(0); }}
            className="text-[11px] text-muted-foreground/70 underline underline-offset-2 hover:text-foreground"
            data-testid="taste-setup-replay-walkthrough"
          >
            Replay walkthrough
          </button>
        </div>
      )}

      {/* First-time coach-mark walkthrough */}
      <AnimatePresence>
        {walkStep >= 0 && spot && (
          <motion.div
            className="fixed inset-0 z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            data-testid="taste-setup-walkthrough"
          >
            {/* spotlight hole via massive box-shadow */}
            <div
              className="absolute rounded-2xl pointer-events-none transition-all duration-300"
              style={{
                top: spot.top - 8, left: spot.left - 8,
                width: spot.width + 16, height: spot.height + 16,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.72)',
                border: '2px solid rgba(255,255,255,0.85)',
              }}
            />
            {/* tooltip */}
            <div
              className="absolute left-4 right-4 flex justify-center"
              style={placeBelow
                ? { top: spot.top + spot.height + 16 }
                : { bottom: (typeof window !== 'undefined' ? window.innerHeight : 800) - spot.top + 16 }}
            >
              <div className="w-full max-w-xs rounded-2xl bg-background p-4 shadow-2xl" data-testid="taste-setup-walkthrough-tip">
                <p className="font-heading font-bold text-sm mb-1">{steps[walkStep].title}</p>
                <p className="text-sm text-muted-foreground">{steps[walkStep].text}</p>
                <div className="flex items-center justify-between mt-4">
                  <div className="flex gap-1.5">
                    {steps.map(function (_s, i) {
                      return <span key={i} className={'w-1.5 h-1.5 rounded-full ' + (i === walkStep ? 'bg-accent' : 'bg-border')} />;
                    })}
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={endWalk} className="text-xs text-muted-foreground hover:text-foreground" data-testid="taste-setup-walkthrough-skip">Skip</button>
                    <button
                      onClick={function () { walkStep >= steps.length - 1 ? endWalk() : setWalkStep(walkStep + 1); }}
                      className="px-4 py-1.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold hover:opacity-90"
                      data-testid="taste-setup-walkthrough-next"
                    >
                      {walkStep >= steps.length - 1 ? 'Got it' : 'Next'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default TasteSetupCards;
